<template>
  <div class="campaign-list">
    <!-- Header -->
    <div class="list-header">
      <div class="filtros">
        <select v-model="filtros.canal" class="select-filtro" @change="carregarLista">
          <option value="">Todos os canais</option>
          <option value="email">Email</option>
          <option value="whatsapp">WhatsApp</option>
        </select>
        <select v-model="filtros.status" class="select-filtro" @change="carregarLista">
          <option value="">Todos os status</option>
          <option value="rascunho">Rascunho</option>
          <option value="agendado">Agendado</option>
          <option value="em_andamento">Em andamento</option>
          <option value="concluido">Concluído</option>
          <option value="erro">Com erro</option>
          <option value="cancelado">Cancelado</option>
        </select>
      </div>
      <button class="btn-criar" @click="$emit('criar')">
        <i class="fas fa-plus"></i> Nova Campanha
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <span>Carregando campanhas...</span>
    </div>

    <!-- Lista vazia -->
    <div v-else-if="campanhas.length === 0" class="empty-state">
      <i class="fas fa-paper-plane"></i>
      <p>Nenhuma campanha encontrada</p>
      <button class="btn-criar-empty" @click="$emit('criar')">
        Criar primeira campanha
      </button>
    </div>

    <!-- Lista de campanhas -->
    <div v-else class="campanhas-table-wrapper">
      <table class="campanhas-table">
        <thead>
          <tr>
            <th>Campanha</th>
            <th>Canal</th>
            <th>Status</th>
            <th>Destinatários</th>
            <th>Enviados</th>
            <th>Falhas</th>
            <th>Criada em</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="campanha in campanhas" :key="campanha.id">
            <td class="col-nome">
              <strong>{{ campanha.nome }}</strong>
              <span v-if="campanha.template" class="template-ref">
                {{ campanha.template.nome }}
              </span>
            </td>
            <td>
              <span class="canal-badge" :class="campanha.canal">
                <i :class="getIconeCanal(campanha.canal)"></i>
                {{ getCanalLabel(campanha.canal) }}
              </span>
            </td>
            <td class="col-status">
              <span
                class="status-badge"
                :class="[campanha.status, { 'has-tooltip': campanha.status === 'agendado' && campanha.agendado_para }]"
                @mouseenter="showScheduleTooltip($event, campanha)"
                @mouseleave="hideScheduleTooltip"
              >
                <i v-if="campanha.status === 'em_andamento'" class="fas fa-sync-alt fa-spin"></i>
                <i v-else-if="campanha.status === 'agendado'" class="fas fa-clock"></i>
                {{ getStatusLabel(campanha.status) }}
              </span>
              <!-- Tooltip para campanhas agendadas -->
              <div
                v-if="tooltipVisible && tooltipCampaign?.id === campanha.id"
                class="schedule-tooltip"
                :style="tooltipStyle"
              >
                <div class="tooltip-row">
                  <i class="fas fa-calendar-alt"></i>
                  <span>{{ formatarAgendamento(campanha.agendado_para) }}</span>
                </div>
                <div class="tooltip-row tooltip-relative">
                  <i class="fas fa-hourglass-half"></i>
                  <span>{{ tempoRelativo(campanha.agendado_para) }}</span>
                </div>
              </div>
            </td>
            <td class="col-number">{{ campanha.total_destinatarios }}</td>
            <td class="col-number col-success">{{ campanha.total_enviados }}</td>
            <td class="col-number col-error">{{ campanha.total_falhas }}</td>
            <td class="col-date">{{ formatarData(campanha.created_at) }}</td>
            <td class="col-actions">
              <button
                v-if="podeEnviar(campanha)"
                class="btn-action btn-enviar"
                title="Enviar agora"
                @click="$emit('enviar', campanha)"
              >
                <i class="fas fa-paper-plane"></i>
              </button>
              <button
                v-if="podeCancelar(campanha)"
                class="btn-action btn-cancelar-camp"
                title="Cancelar"
                @click="confirmarCancelar(campanha)"
              >
                <i class="fas fa-stop"></i>
              </button>
              <button
                v-if="podeReenviar(campanha)"
                class="btn-action btn-reenviar"
                title="Reenviar"
                @click="confirmarReenvio(campanha)"
              >
                <i class="fas fa-redo"></i>
              </button>
              <button
                v-if="podeDuplicar(campanha)"
                class="btn-action btn-duplicar"
                title="Duplicar campanha"
                @click="confirmarDuplicar(campanha)"
              >
                <i class="fas fa-copy"></i>
              </button>
              <button
                v-if="podeEditar(campanha)"
                class="btn-action"
                title="Editar"
                @click="$emit('editar', campanha)"
              >
                <i class="fas fa-edit"></i>
              </button>
              <button
                class="btn-action"
                title="Ver detalhes"
                @click="$emit('detalhes', campanha)"
              >
                <i class="fas fa-eye"></i>
              </button>
              <button
                v-if="podeExcluir(campanha)"
                class="btn-action btn-danger"
                title="Excluir"
                @click="confirmarExclusao(campanha)"
              >
                <i class="fas fa-trash"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Paginação -->
      <div v-if="paginacao.last_page > 1" class="paginacao">
        <button
          class="btn-page"
          :disabled="paginacao.current_page === 1"
          @click="mudarPagina(1)"
        >
          <i class="fas fa-angle-double-left"></i>
        </button>
        <button
          class="btn-page"
          :disabled="paginacao.current_page === 1"
          @click="mudarPagina(paginacao.current_page - 1)"
        >
          <i class="fas fa-angle-left"></i>
        </button>
        <span class="page-info">
          Página {{ paginacao.current_page }} de {{ paginacao.last_page }}
          ({{ paginacao.total }} campanhas)
        </span>
        <button
          class="btn-page"
          :disabled="paginacao.current_page === paginacao.last_page"
          @click="mudarPagina(paginacao.current_page + 1)"
        >
          <i class="fas fa-angle-right"></i>
        </button>
        <button
          class="btn-page"
          :disabled="paginacao.current_page === paginacao.last_page"
          @click="mudarPagina(paginacao.last_page)"
        >
          <i class="fas fa-angle-double-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import Swal from 'sweetalert2'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/pt-br'

dayjs.extend(relativeTime)
dayjs.locale('pt-br')

const props = defineProps({
  campanhas: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  paginacao: {
    type: Object,
    default: () => ({
      current_page: 1,
      last_page: 1,
      per_page: 15,
      total: 0,
    }),
  },
})

// Tooltip state para campanhas agendadas
const tooltipVisible = ref(false)
const tooltipCampaign = ref(null)
const tooltipStyle = ref({})

const emit = defineEmits(['criar', 'editar', 'excluir', 'enviar', 'reenviar', 'cancelar', 'detalhes', 'carregar', 'duplicar'])

const filtros = ref({
  canal: '',
  status: '',
})

function carregarLista(pagina = 1) {
  emit('carregar', filtros.value, pagina)
}

function mudarPagina(pagina) {
  carregarLista(pagina)
}

function getIconeCanal(canal) {
  return canal === 'email' ? 'fas fa-envelope' : 'fab fa-whatsapp'
}

function getCanalLabel(canal) {
  return canal === 'email' ? 'Email' : 'WhatsApp'
}

function getStatusLabel(status) {
  const labels = {
    rascunho: 'Rascunho',
    agendado: 'Agendado',
    em_andamento: 'Em andamento',
    concluido: 'Concluído',
    erro: 'Erro',
    cancelado: 'Cancelado',
  }
  return labels[status] || status
}

function formatarData(data) {
  if (!data) return '-'
  return dayjs(data).format('DD/MM/YYYY HH:mm')
}

function formatarAgendamento(data) {
  if (!data) return '-'
  return dayjs(data).format('DD/MM/YYYY [às] HH:mm')
}

function tempoRelativo(data) {
  if (!data) return '-'
  return dayjs(data).fromNow()
}

function showScheduleTooltip(event, campanha) {
  if (campanha.status !== 'agendado' || !campanha.agendado_para) return

  const rect = event.target.getBoundingClientRect()
  tooltipStyle.value = {
    top: `${rect.bottom + 8}px`,
    left: `${rect.left}px`,
  }
  tooltipCampaign.value = campanha
  tooltipVisible.value = true
}

function hideScheduleTooltip() {
  tooltipVisible.value = false
  tooltipCampaign.value = null
}

function podeEnviar(campanha) {
  return ['rascunho', 'agendado'].includes(campanha.status)
}

function podeCancelar(campanha) {
  return ['agendado', 'em_andamento'].includes(campanha.status)
}

function podeEditar(campanha) {
  return ['rascunho', 'agendado'].includes(campanha.status)
}

function podeReenviar(campanha) {
  return ['erro', 'cancelado'].includes(campanha.status)
}

function podeDuplicar(campanha) {
  return campanha.status === 'concluido'
}

function podeExcluir(campanha) {
  return campanha.status !== 'em_andamento'
}

async function confirmarDuplicar(campanha) {
  const result = await Swal.fire({
    title: 'Duplicar campanha?',
    text: `Uma nova campanha será criada a partir de "${campanha.nome}".`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#1976d2',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Sim, duplicar',
    cancelButtonText: 'Cancelar',
  })

  if (result.isConfirmed) {
    emit('duplicar', campanha)
  }
}

async function confirmarReenvio(campanha) {
  const result = await Swal.fire({
    title: 'Reenviar campanha?',
    text: `A campanha "${campanha.nome}" será reenviada.`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#f57c00',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Sim, reenviar',
    cancelButtonText: 'Cancelar',
  })

  if (result.isConfirmed) {
    emit('reenviar', campanha)
  }
}

async function confirmarExclusao(campanha) {
  const result = await Swal.fire({
    title: 'Excluir campanha?',
    text: `A campanha "${campanha.nome}" será excluída permanentemente.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#dc3545',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Sim, excluir',
    cancelButtonText: 'Cancelar',
  })

  if (result.isConfirmed) {
    emit('excluir', campanha)
  }
}

async function confirmarCancelar(campanha) {
  const result = await Swal.fire({
    title: 'Cancelar campanha?',
    text: `O envio da campanha "${campanha.nome}" será interrompido.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ffc107',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Sim, cancelar',
    cancelButtonText: 'Voltar',
  })

  if (result.isConfirmed) {
    emit('cancelar', campanha)
  }
}

onMounted(() => {
  carregarLista()
})
</script>

<style scoped>
.campaign-list {
  padding: 20px;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;
}

.filtros {
  display: flex;
  gap: 10px;
}

.select-filtro {
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  cursor: pointer;
}

.btn-criar {
  padding: 10px 20px;
  background: #4caf50;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s;
}

.btn-criar:hover {
  background: #388e3c;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: #666;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4caf50;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #666;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 15px;
  color: #ccc;
}

.btn-criar-empty {
  margin-top: 15px;
  padding: 10px 20px;
  background: #4caf50;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.campanhas-table-wrapper {
  overflow-x: auto;
}

.campanhas-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.campanhas-table th,
.campanhas-table td {
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.campanhas-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #555;
  white-space: nowrap;
}

.campanhas-table tr:hover {
  background: #f8f9fa;
}

.col-nome {
  min-width: 200px;
}

.col-nome strong {
  display: block;
  color: #333;
}

.template-ref {
  font-size: 12px;
  color: #888;
}

.col-number {
  text-align: center;
  font-weight: 600;
}

.col-success {
  color: #388e3c;
}

.col-error {
  color: #c62828;
}

.col-date {
  white-space: nowrap;
  color: #666;
}

.col-actions {
  white-space: nowrap;
}

.canal-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.canal-badge.email {
  background: #e3f2fd;
  color: #1976d2;
}

.canal-badge.whatsapp {
  background: #e8f5e9;
  color: #388e3c;
}

.status-badge {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
}

.status-badge.rascunho {
  background: #f5f5f5;
  color: #757575;
}

.status-badge.agendado {
  background: #e3f2fd;
  color: #1976d2;
}

.status-badge.has-tooltip {
  cursor: pointer;
}

.status-badge.has-tooltip:hover {
  background: #bbdefb;
}

.col-status {
  position: relative;
}

.schedule-tooltip {
  position: fixed;
  background: #1e1e1e;
  color: #fff;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 13px;
  z-index: 10000;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  min-width: 200px;
  pointer-events: none;
}

.schedule-tooltip::before {
  content: '';
  position: absolute;
  top: -6px;
  left: 20px;
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-bottom: 6px solid #1e1e1e;
}

.tooltip-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 0;
}

.tooltip-row i {
  width: 16px;
  text-align: center;
  color: #64b5f6;
}

.tooltip-row.tooltip-relative i {
  color: #81c784;
}

.tooltip-row.tooltip-relative span {
  text-transform: capitalize;
}

.status-badge.em_andamento {
  background: #fff3e0;
  color: #f57c00;
}

.status-badge.concluido {
  background: #e8f5e9;
  color: #388e3c;
}

.status-badge.erro {
  background: #ffebee;
  color: #c62828;
}

.status-badge.cancelado {
  background: #f5f5f5;
  color: #757575;
}

.btn-action {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: #f5f5f5;
  color: #666;
  cursor: pointer;
  margin-right: 4px;
  transition: all 0.2s;
}

.btn-action:hover {
  background: #e0e0e0;
  color: #333;
}

.btn-action.btn-enviar {
  background: #e8f5e9;
  color: #388e3c;
}

.btn-action.btn-enviar:hover {
  background: #c8e6c9;
}

.btn-action.btn-reenviar {
  background: #fff3e0;
  color: #f57c00;
}

.btn-action.btn-reenviar:hover {
  background: #ffe0b2;
}

.btn-action.btn-duplicar {
  background: #e3f2fd;
  color: #1976d2;
}

.btn-action.btn-duplicar:hover {
  background: #bbdefb;
}

.btn-action.btn-cancelar-camp {
  background: #fff3e0;
  color: #f57c00;
}

.btn-action.btn-cancelar-camp:hover {
  background: #ffe0b2;
}

.btn-action.btn-danger:hover {
  background: #ffebee;
  color: #c62828;
}

/* Paginação */
.paginacao {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.btn-page {
  width: 36px;
  height: 36px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  transition: all 0.2s;
}

.btn-page:hover:not(:disabled) {
  background: #f5f5f5;
  border-color: #ccc;
}

.btn-page:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #666;
  padding: 0 10px;
}

/* Dark mode */
.dark-mode .campanhas-table th {
  background: #2a2a2a;
  color: #ccc;
}

.dark-mode .campanhas-table td {
  border-color: #444;
}

.dark-mode .campanhas-table tr:hover {
  background: #2a2a2a;
}

.dark-mode .col-nome strong {
  color: #f1f1f1;
}

.dark-mode .select-filtro {
  background: #2a2a2a;
  border-color: #444;
  color: #f1f1f1;
}

.dark-mode .paginacao {
  border-top-color: #444;
}

.dark-mode .btn-page {
  background: #2a2a2a;
  border-color: #444;
  color: #ccc;
}

.dark-mode .btn-page:hover:not(:disabled) {
  background: #333;
  border-color: #555;
}

.dark-mode .page-info {
  color: #aaa;
}

@media (max-width: 768px) {
  .list-header {
    flex-direction: column;
    align-items: stretch;
  }

  .filtros {
    flex-direction: column;
  }

  .select-filtro {
    width: 100%;
  }

  .btn-criar {
    width: 100%;
    justify-content: center;
  }

  .campanhas-table {
    font-size: 12px;
  }

  .campanhas-table th,
  .campanhas-table td {
    padding: 8px 10px;
  }

  .paginacao {
    flex-wrap: wrap;
  }

  .page-info {
    width: 100%;
    text-align: center;
    order: -1;
    margin-bottom: 10px;
  }
}
</style>
